import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  Clock,
  MapPin,
  Wine,
  Wrench,
  Activity,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Footprints,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Mareil-Marly ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de vos fournitures de déménagement {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que transporter des paquets de cartons plats et
              encombrants dans votre véhicule est une corvée dont vous
              pouvez vous passer, notre service de livraison
              s&apos;invite directement chez vous.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture locale complète",
                body: "Nous acheminons l'intégralité de vos fournitures directement sur le pas de votre porte à Mareil-Marly, mais aussi à Marly-le-Roi, L'Étang-la-Ville, Saint-Germain-en-Laye et Le Pecq.",
                Icon: MapPin,
              },
              {
                title: "La garantie du bon créneau",
                body: "Nous convenons ensemble d'un horaire précis adapté à votre emploi du temps pour vous éviter de perdre votre journée à attendre le livreur.",
                Icon: Clock,
              },
              {
                title: "Un matériel impeccable",
                body: "Vos fournitures vous sont remises au sec, parfaitement propres et prêtes à être pliées pour vous permettre de cartonner sans attendre.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Arts de la table et grands crus ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Pièces fragiles
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Arts de la table et grands crus : sécurisez vos pièces
            sensibles
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Mareil-Marly abrite de magnifiques propriétés dotées de
            belles cuisines et parfois de caves réputées. Ne prenez
            aucun risque avec vos objets en verre ou vos bouteilles
            de valeur.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Croisillons vaisselle */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Boxes className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Les cartons à croisillons pour la vaisselle
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Fini le temps perdu à envelopper chaque verre dans du
              papier journal. Nos cartons spécialisés intègrent des
              alvéoles rigides en carton qui isolent chaque verre,
              tasse ou assiette. Les chocs thermiques et les
              vibrations de la route sont totalement absorbés,
              garantissant un déballage zéro casse.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Alvéoles rigides intégrées
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Isolation verres / tasses / assiettes
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Déballage zéro casse
              </div>
            </div>
            <Link
              to="/cartons-demenagement"
              className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[13px] transition-colors"
              data-testid="city-mareil-marly-cta-croisillons"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons vaisselle
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          {/* Bouteilles */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Wine className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Les caisses renforcées pour bouteilles
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Pour vos bouteilles de vin ou de spiritueux, utilisez
              nos modules spécifiques équipés d&apos;inserts de
              calage. Ils maintiennent les bouteilles debout et
              espacées, évitant les entrechocs destructeurs durant les
              virages ou les freinages du camion de déménagement.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Inserts de calage rigides
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Bouteilles maintenues debout
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Anti-entrechocs en transport
              </div>
            </div>
            <Link
              to="/cartons-demenagement"
              className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[13px] transition-colors"
              data-testid="city-mareil-marly-cta-bouteilles"
            >
              <Wine className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons bouteilles
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Ergonomie & sécurité ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Ergonomie & sécurité
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Ergonomie et sécurité : l&apos;art de manipuler les cartons
            lourds
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un déménagement réussi est un déménagement où l&apos;on se
            préserve physiquement. La manipulation des cartons de
            livres ou d&apos;archives demande de bons réflexes pour
            éviter les blessures.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Test du poids — orange (danger) */}
          <div className="relative bg-white border border-[#cfdbe6] rounded-2xl overflow-hidden">
            <span className="absolute top-0 left-0 h-1.5 w-full bg-[#FF5722]" />
            <div className="p-6 md:p-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFF1EC] border border-[#FFD3C0] text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#FF5722] mb-4">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.8} />
                Conseil n°01
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#FF5722] text-white">
                  <Activity className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="font-display font-extrabold text-[19px] text-[#0f1d2b] leading-tight">
                  Le test du poids avant le levage
                </h3>
              </div>
              <p className="mt-5 text-[14.5px] text-[#3a4856] leading-relaxed">
                Ne présumez jamais de la légèreté d&apos;un colis.
                Avant de soulever un carton, poussez-le légèrement du
                pied pour évaluer sa masse. Si vous avez utilisé nos
                cartons spécial &laquo;&nbsp;Livres&nbsp;&raquo; (plus
                petits mais très denses), veillez à ce qu&apos;ils ne
                dépassent jamais 15 à 20 kg pour rester maniables par
                une seule personne.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FFF1EC] text-[#FF5722] font-extrabold text-[13px]">
                <AlertTriangle className="h-4 w-4" strokeWidth={2.6} />
                Max conseillé : 15-20 kg / carton livres
              </div>
            </div>
          </div>

          {/* Posture — bleu (best practice) */}
          <div className="relative bg-white border border-[#cfdbe6] rounded-2xl overflow-hidden">
            <span className="absolute top-0 left-0 h-1.5 w-full bg-[#237cb5]" />
            <div className="p-6 md:p-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EEF3F7] border border-[#cfdbe6] text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#237cb5] mb-4">
                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.8} />
                Conseil n°02
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                  <Footprints className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="font-display font-extrabold text-[19px] text-[#0f1d2b] leading-tight">
                  La bonne posture de manutention
                </h3>
              </div>
              <p className="mt-5 text-[14.5px] text-[#3a4856] leading-relaxed">
                Pour ramasser un carton au sol, ne courbez jamais le
                dos en gardant les jambes tendues. Écartez les pieds
                pour stabiliser vos appuis, fléchissez les genoux pour
                vous accroupir, et gardez le dos parfaitement droit.
                Soulevez la charge en poussant sur vos cuisses et
                maintenez le carton le plus près possible de votre
                corps. C&apos;est le secret des déménageurs
                professionnels pour enchaîner les allers-retours sans
                fatigue musculaire.
              </p>
              <ul className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Pieds écartés, appuis stables
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Genoux fléchis, dos droit
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Charge maintenue près du corps
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-mareil-marly-cta-diable"
          >
            <Wrench className="h-4 w-4" strokeWidth={2.6} />
            Diables &amp; chariots à roulettes
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-mareil-marly-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-mareil-marly-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
